import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { getServiceRequestById, updateServiceRequest, UpdateServiceRequestData } from '../../../services/serviceRequestService';
import { RequestStatus, PaymentStatus, ServiceRequest } from '../../../types/serviceRequest';
import formStyles from '../trainees/TraineeForm.module.css';

type FormInputs = Required<UpdateServiceRequestData>;

const EditServiceRequestPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const requestId = Number(id);

  const [requestDetails, setRequestDetails] = useState<ServiceRequest | null>(null);

  const { 
    register, 
    handleSubmit, 
    reset, 
    formState: { errors, isSubmitting } 
  } = useForm<FormInputs>();

  useEffect(() => {
    if (!requestId) return;
    const fetchRequestData = async () => {
      try {
        const data = await getServiceRequestById(requestId);
        setRequestDetails(data);
        reset({
          status: data.status,
          payment_status: data.payment_status,
        });
      } catch (error) {
        toast.error('فشل في جلب بيانات الطلب.');
        navigate('/admin/service-requests/list');
      }
    };
    fetchRequestData();
  }, [requestId, navigate, reset]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    const promise = updateServiceRequest(requestId, data);
    toast.promise(promise, {
      loading: 'جاري تحديث الطلب...',
      success: () => {
        navigate('/admin/service-requests/list');
        return 'تم تحديث الطلب بنجاح!';
      },
      error: (err) => err.response?.data?.message || 'فشل في تحديث البيانات.',
    });
  };

  return (
    <div className={formStyles.pageContainer}>
      <div className={formStyles.formContainer}>
        <h2 className={formStyles.title}>تفاصيل الطلب رقم: {requestDetails?.id}</h2>
        
        {/* قسم لعرض المعلومات الثابتة */}
        <div className={formStyles.detailsSection}>
          <p><strong>الخدمة:</strong> {requestDetails?.service?.name}</p>
          <p><strong>المستخدم:</strong> {requestDetails?.user?.name}</p>
          <p><strong>تاريخ الإنشاء:</strong> {requestDetails ? new Date(requestDetails.created_at).toLocaleString('ar-EG') : ''}</p>
          <p><strong>الرسوم:</strong> {requestDetails?.fee} ر.ي</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className={formStyles.form}>
          <div className={formStyles.formField}>
            <label htmlFor="status" className={formStyles.label}>تغيير حالة الطلب</label>
            <select id="status" {...register('status')} className={formStyles.input}>
              {Object.values(RequestStatus).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className={formStyles.formField}>
            <label htmlFor="payment_status" className={formStyles.label}>تغيير حالة الدفع</label>
            <select id="payment_status" {...register('payment_status')} className={formStyles.input}>
              {Object.values(PaymentStatus).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className={`${formStyles.buttonContainer} ${formStyles.fullWidth}`}>
            <Link to="/admin/service-requests/list" className={formStyles.cancelButton}>العودة للقائمة</Link>
            <button type="submit" disabled={isSubmitting} className={formStyles.submitButton}>
              {isSubmitting ? 'جاري الحفظ...' : 'حفظ التغييرات'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditServiceRequestPage;
